import { BillingStrategy, type BillingStrategyQuery } from '@ctct/domain-layer';

import { useIsStripeEnabled } from '../experiments';
import { useIsStripePhase2Enabled } from '../experiments/useIsStripePhase2Enabled';
import { useBillingAccount, useIsTrialer } from '../queries';

export type { BillingStrategyQuery };

function shouldShowStripe({
  isTrialer,
  isStripeViaBillingAccount,
  isStripeEnabledViaStatsig,
}: {
  isTrialer?: boolean;
  isStripeViaBillingAccount?: boolean;
  isStripeEnabledViaStatsig?: boolean;
}) {
  if (!isTrialer) {
    return isStripeViaBillingAccount;
  }

  return isStripeViaBillingAccount || isStripeEnabledViaStatsig;
}

type BillingStrategyMetadata = {
  isError: boolean;
  error: Error | null | undefined;
  phase2Enabled: boolean | null | undefined;
  isStripeViaBillingAccount: boolean | undefined;
};

export type BillingStrategyDetails = BillingStrategyQuery & BillingStrategyMetadata;

export const useBillingStrategy = (): BillingStrategyDetails => {
  const isStripePhase2Enabled = useIsStripePhase2Enabled();
  const billingAccount = useBillingAccount();
  const isTrialer = useIsTrialer();
  const isStripeEnabledViaStatsig = useIsStripeEnabled(isTrialer.data);

  const error =
    isStripeEnabledViaStatsig.error || billingAccount.error || isTrialer.error || isStripePhase2Enabled.error;

  const isLoading =
    isStripeEnabledViaStatsig.isLoading ||
    billingAccount.isLoading ||
    isTrialer.isLoading ||
    isStripePhase2Enabled.isLoading;

  const isStripeViaBillingAccount = billingAccount.data?.getIsStripeAccount();

  if (isLoading || error) {
    return {
      data: null,
      isPending: true,
      isError: !!error,
      error,
      phase2Enabled: false,
      isStripeViaBillingAccount,
    };
  }

  const data = shouldShowStripe({
    isTrialer: isTrialer.data,
    isStripeViaBillingAccount,
    isStripeEnabledViaStatsig: isStripeEnabledViaStatsig.data,
  })
    ? BillingStrategy.Stripe
    : BillingStrategy.InHouse;

  return {
    data,
    isPending: false,
    isError: false,
    error: null,
    phase2Enabled: data === BillingStrategy.Stripe && isStripePhase2Enabled.data,
    isStripeViaBillingAccount,
  };
};
